import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Mic, Sparkles, Loader2, Upload, Play, Pause, Trash2, Download,
  Wand2, Music2, Zap, Library as LibraryIcon, Save, Volume2, Scissors, Rss, Layers, Radio, Layers3, Rocket, Share2, Pencil, Check, X
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SequencerTab from "@/components/SequencerTab";
import PromptSuggestionButton from "@/components/PromptSuggestionButton";
import MusicSourcePicker from "@/components/MusicSourcePicker";
import MorningPackPanel from "@/components/MorningPackPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import AudioTrimmer from "@/components/AudioTrimmer";
import OwnVoiceRecorder from "@/components/OwnVoiceRecorder";
import WatermarkUnlockPrompt from "@/components/WatermarkUnlockPrompt";
import SfxBrowser from "@/components/SfxBrowser";
import StemSeparateModal from "@/components/production/StemSeparateModal";
import KaraokeModal from "@/components/production/KaraokeModal";
import KaraokeShareModal from "@/components/production/KaraokeShareModal";

const KIND_LABEL = { bed: "Music bed", fx: "Sound FX", voice: "Voice take", opener: "Opener", closer: "Closer" };
const BED_CATEGORIES = ["All", "R&B", "Urban", "Pop", "Reggae", "Hip-Hop", "News", "Sports", "Talk Radio", "Smooth Jazz", "Dance", "Gospel", "Caribbean"];

const SOURCE_LABEL = {
  user_uploaded:    "Uploaded",
  internet_archive: "Internet Archive",
  jamendo:          "Jamendo",
  freesound:        "Freesound",
  pixabay_image:    "Pixabay",
  mubert:           "AI · Mubert",
  suno:             "AI · Suno",
  ai_generated:     "AI Generated",
};

function SourceBadge({ source, testId }) {
  const label = SOURCE_LABEL[source] || source;
  return (
    <span
      className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-wider"
      style={{ background: "#1a3a5c", color: "#4a9eff" }}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

function DailyBedPackButton({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);
  const fetchPack = async () => {
    setBusy(true);
    toast.message("Fetching today's bed pack — one fresh track per genre (this can take ~60s)");
    try {
      const r = await api.post("/imaging/library/daily-pack", null, { timeout: 180000 });
      const d = r.data || {};
      onImported?.(d.imported || []);
      const lines = [];
      if (d.count) lines.push(`Imported ${d.count} from ${d.primary_source}`);
      if ((d.skipped_recently_imported || []).length) lines.push(`Skipped ${d.skipped_recently_imported.length} (recent)`);
      if ((d.errors || []).length) lines.push(`${d.errors.length} skipped (no results)`);
      toast.success(lines.join(" · ") || "Pack ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Daily pack failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={fetchPack}
      disabled={busy}
      data-testid="daily-bed-pack-btn"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 ml-auto rounded-md text-xs font-semibold transition disabled:opacity-80"
      style={{ background: "linear-gradient(135deg, #4a9eff 0%, #6b5cf6 100%)", color: "#0a0d14" }}
      title="Auto-import one fresh track per genre from Jamendo (skips any imported in the last 18 hours)"
    >
      {busy
        ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Curating · {elapsed}s</>
        : <>✨ My Daily Bed Pack</>
      }
    </button>
  );
}

export default function Imaging() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "script";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (v) => {
    setTab(v);
    if (searchParams.get("tab")) {
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="imaging-page">
      <PageHeader
        eyebrow="// IMAGING STUDIO"
        title="Sound the room. Brand the air."
        description="Generate station IDs, sweepers, jingles, DJ drops, podcast intros & promos. Scripts, voices, beds, FX — mixed in one place."
      />

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="bg-bg-surface border border-line p-1 rounded-md mb-6 flex flex-wrap" data-testid="imaging-tabs">
          <TabsTrigger value="script"   data-testid="tab-im-script"   className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Wand2 className="h-3.5 w-3.5 mr-1.5" />Script</TabsTrigger>
          <TabsTrigger value="trim"     data-testid="tab-im-trim"     className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Scissors className="h-3.5 w-3.5 mr-1.5" />Trim & Edit</TabsTrigger>
          <TabsTrigger value="voice"    data-testid="tab-im-voice"    className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Mic className="h-3.5 w-3.5 mr-1.5" />Voice</TabsTrigger>
          <TabsTrigger value="sequencer" data-testid="tab-im-sequencer" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Layers className="h-3.5 w-3.5 mr-1.5" />Sequencer</TabsTrigger>
          <TabsTrigger value="assets"   data-testid="tab-im-assets"   className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Music2 className="h-3.5 w-3.5 mr-1.5" />Beds & FX</TabsTrigger>
          <TabsTrigger value="mixer"    data-testid="tab-im-mixer"    className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><Volume2 className="h-3.5 w-3.5 mr-1.5" />Mixer</TabsTrigger>
          <TabsTrigger value="library"  data-testid="tab-im-library"  className="data-[state=active]:bg-brand-amber data-[state=active]:text-black"><LibraryIcon className="h-3.5 w-3.5 mr-1.5" />Library</TabsTrigger>
        </TabsList>
        <TabsContent value="script"><ScriptTab /></TabsContent>
        <TabsContent value="trim"><AudioTrimmer /></TabsContent>
        <TabsContent value="voice"><VoiceTab /></TabsContent>
        <TabsContent value="sequencer"><SequencerTab /></TabsContent>
        <TabsContent value="assets"><AssetsTab /></TabsContent>
        <TabsContent value="mixer"><MixerTab /></TabsContent>
        <TabsContent value="library"><LibraryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =====================================================
 * SCRIPT TAB
 * ===================================================== */
function ScriptTab() {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    piece_type: "station_id",
    station_name: "",
    dj_name: "",
    show_name: "",
    tone: "energetic",
    extra_context: "",
  });
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplLabel, setTplLabel] = useState("");

  const loadTemplates = async () => {
    try {
      const res = await api.get("/imaging/templates");
      setTemplates(res.data);
    } catch {}
  };

  useEffect(() => {
    api.get("/imaging/types").then((r) => setTypes(r.data));
    api.get("/stations/me").then((r) => {
      if (r.data?.name) setForm((f) => ({ ...f, station_name: r.data.name }));
    });
    loadTemplates();
  }, []);

  const run = async () => {
    if (!form.station_name.trim()) { toast.error("Station name required"); return; }
    setLoading(true);
    setScript("");
    try {
      const res = await api.post("/imaging/scripts/generate", form);
      setScript(res.data.script);
      toast.success("Script ready");
    } catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  const saveTemplate = async () => {
    if (!tplLabel.trim() || !script.trim()) { toast.error("Label and script required"); return; }
    try {
      await api.post("/imaging/templates", {
        label: tplLabel,
        piece_type: form.piece_type,
        body: script,
        is_prompt: false,
      });
      toast.success("Template saved");
      setShowSaveTpl(false);
      setTplLabel("");
      loadTemplates();
    } catch { toast.error("Save failed"); }
  };

  const applyTemplate = (t) => {
    setScript(t.body);
    setForm((f) => ({ ...f, piece_type: t.piece_type }));
    toast.success(`Loaded template: ${t.label}`);
  };

  const deleteTemplate = async (id) => {
    await api.delete(`/imaging/templates/${id}`);
    setTemplates(templates.filter((t) => t.template_id !== id));
    toast.success("Removed");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="im-script-form">
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Imaging piece type</Label>
          <Select value={form.piece_type} onValueChange={(v) => setForm({ ...form, piece_type: v })}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="im-piece-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Station name *" v={form.station_name} on={(x) => setForm({ ...form, station_name: x })} testid="im-station-name" />
          <Inp label="DJ name" v={form.dj_name} on={(x) => setForm({ ...form, dj_name: x })} testid="im-dj-name" />
          <Inp label="Show name" v={form.show_name} on={(x) => setForm({ ...form, show_name: x })} testid="im-show-name" />
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tone</Label>
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="im-tone"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                {["energetic", "bold", "smooth", "authoritative", "playful", "hype", "moody"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Extra context</Label>
            <PromptSuggestionButton
              surface="imaging"
              subtype={form.piece_type}
              fieldName="topic"
              context={{ station_name: form.station_name, dj_name: form.dj_name, show_name: form.show_name, tone: form.tone, piece_type: form.piece_type }}
              onSelect={(v) => setForm((s) => ({ ...s, extra_context: v }))}
              testid="im-suggest-context"
            />
          </div>
          <Textarea value={form.extra_context} onChange={(e) => setForm({ ...form, extra_context: e.target.value })} rows={3} className="mt-1 bg-bg border-line" data-testid="im-extra" />
        </div>
        <button onClick={run} disabled={loading} data-testid="im-script-generate" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Writing..." : "Generate script"}
        </button>

        {/* Templates */}
        <div className="pt-3 border-t border-line">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">// SAVED TEMPLATES · {templates.length}</div>
          </div>
          {templates.length === 0 ? (
            <div className="type-body text-ink-muted">No templates yet. Generate a script, then click "Save as template".</div>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-auto no-scrollbar">
              {templates.map((t) => (
                <div key={t.template_id} className="flex items-center justify-between rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs" data-testid={`tpl-${t.template_id}`}>
                  <button onClick={() => applyTemplate(t)} data-testid={`tpl-use-${t.template_id}`} className="flex-1 text-left truncate hover:text-cyan-300 transition">
                    <span className="font-medium">{t.label}</span>
                    <span className="text-ink-muted ml-2 font-mono text-[10px]">· {t.piece_type.replace(/_/g, " ")}</span>
                  </button>
                  <button onClick={() => deleteTemplate(t.template_id)} className="text-ink-muted hover:text-brand-onair ml-2" data-testid={`tpl-del-${t.template_id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div data-testid="im-script-output">
        {!loading && !script && (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted type-body">
            Your imaging script will appear here. Send it to the Voice tab to record.
          </div>
        )}
        {(loading || script) && (
          <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">// SCRIPT · {form.piece_type.replace(/_/g, " ")}</div>
              {script && !showSaveTpl && (
                <button onClick={() => setShowSaveTpl(true)} data-testid="im-save-template" className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono type-status-badge uppercase tracking-wider hover:text-cyan-300 transition">
                  Save as template
                </button>
              )}
            </div>
            <Textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={8}
              className="bg-bg border-line font-sans type-body whitespace-pre-wrap resize-none"
              data-testid="im-script-text"
              placeholder="..."
            />
            {showSaveTpl && (
              <div className="mt-3 flex gap-2 items-center">
                <Input
                  value={tplLabel}
                  onChange={(e) => setTplLabel(e.target.value)}
                  placeholder="Template label e.g. 'Friday late-night ID'"
                  className="bg-bg border-line type-body flex-1"
                  data-testid="im-tpl-label"
                />
                <button onClick={saveTemplate} data-testid="im-tpl-save" className="rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow">Save</button>
                <button onClick={() => { setShowSaveTpl(false); setTplLabel(""); }} className="rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label">Cancel</button>
              </div>
            )}
            {script && !showSaveTpl && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { sessionStorage.setItem("imaging_script", script); toast.success("Sent to Voice tab"); }}
                  data-testid="im-send-to-voice"
                  className="rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow"
                >
                  Send to Voice →
                </button>
                <button
                  onClick={() => { navigator.clipboard.writeText(script); toast.success("Copied"); }}
                  className="rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label"
                >Copy</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================
 * VOICE TAB
 * ===================================================== */
const CART_PIECE_TYPES = [
  { id: "sweeper",       label: "Sweeper" },
  { id: "station_id",    label: "Station ID" },
  { id: "dj_drop",       label: "DJ Drop" },
  { id: "jingle",        label: "Jingle" },
  { id: "promo",         label: "Promo" },
  { id: "podcast_intro", label: "Podcast Intro" },
  { id: "top_of_hour",   label: "Top of Hour" },
  { id: "weather_intro", label: "Weather Intro" },
  { id: "news_intro",    label: "News Intro" },
  { id: "show_opener",   label: "Show Opener" },
  { id: "sponsor_tag",   label: "Sponsor Tag" },
];

function VoiceTab() {
  const [voices, setVoices] = useState([]);
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [speed, setSpeed] = useState(1.0);
  const [hd, setHd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastAsset, setLastAsset] = useState(null);
  const [voiceMode, setVoiceMode] = useState("ai"); // ai | record | upload
  const [cartType, setCartType] = useState("sweeper");
  const [mixWithBed, setMixWithBed] = useState(false);
  const [beds, setBeds] = useState([]);
  const [bedGenre, setBedGenre] = useState("auto");
  const [mixStyle, setMixStyle] = useState("smooth");
  const [mixPresets, setMixPresets] = useState([]);
  const [sendingCart, setSendingCart] = useState(false);
  const [cartResult, setCartResult] = useState(null);
  // Update #107 — one-click "Generate matching bed" (Mubert TTM)
  const [aiBedBusy, setAiBedBusy] = useState(false);
  // Update #108 — "Ship it" mega-button: bed → mix → cart → on-air
  const [shipping, setShipping] = useState(false);
  const [shipStage, setShipStage] = useState("");  // "bed" | "mix" | "cart" | ""
  // Update #109/113 — Series mode: group carts by named series
  // (e.g. "Morning show sweepers"). "" = no series, "__new__" = user typed a
  // brand-new name in the input, or srs_xxx = existing series id.
  const [seriesList, setSeriesList] = useState([]);
  const [seriesSelectedId, setSeriesSelectedId] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("bfa_imaging_series_id")) || ""
  );
  const [seriesNewName, setSeriesNewName] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("bfa_imaging_series_new_name")) || ""
  );

  // AI script generator (in-line)
  const [scriptPieceType, setScriptPieceType] = useState("sweeper");
  const [scriptTone, setScriptTone] = useState("energetic");
  const [scriptBusy, setScriptBusy] = useState(false);
  const [station, setStation] = useState(null);
  const [pieceTypes, setPieceTypes] = useState([]);

  useEffect(() => {
    api.get("/imaging/voices").then((r) => setVoices(r.data));
    api.get("/imaging/mix-presets").then((r) => setMixPresets(r.data || [])).catch(() => {});
    api.get("/imaging/types").then((r) => setPieceTypes(r.data || [])).catch(() => {});
    api.get("/stations/me").then((r) => setStation(r.data)).catch(() => {});
    api.get("/imaging/series").then((r) => setSeriesList(r.data?.items || [])).catch(() => {});
    const saved = sessionStorage.getItem("imaging_script");
    if (saved) { setText(saved); sessionStorage.removeItem("imaging_script"); }
  }, []);

  // Persist series selection so a producer's preferred day-part sticks
  // across page reloads within the same browser.
  useEffect(() => {
    try {
      if (seriesSelectedId) localStorage.setItem("bfa_imaging_series_id", seriesSelectedId);
      else localStorage.removeItem("bfa_imaging_series_id");
    } catch { /* no-op */ }
  }, [seriesSelectedId]);
  useEffect(() => {
    try {
      if (seriesNewName) localStorage.setItem("bfa_imaging_series_new_name", seriesNewName);
      else localStorage.removeItem("bfa_imaging_series_new_name");
    } catch { /* no-op */ }
  }, [seriesNewName]);

  // Refetch the series list after every ship so newly-created series show
  // up in the picker without a page reload.
  const refreshSeries = async () => {
    try {
      const r = await api.get("/imaging/series");
      setSeriesList(r.data?.items || []);
    } catch { /* no-op */ }
  };

  const generateScript = async () => {
    setScriptBusy(true);
    try {
      const { data } = await api.post("/imaging/scripts/generate", {
        piece_type: scriptPieceType,
        station_name: station?.name || "",
        dj_name: "",
        show_name: "",
        tone: scriptTone,
        extra_context: "",
      });
      if (data?.script) {
        setText((data.script || "").slice(0, 4096));
        toast.success("Script written — review then record");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Script generation failed");
    } finally {
      setScriptBusy(false);
    }
  };

  // Lazy-load beds the first time the user toggles "Mix with bed" on.
  useEffect(() => {
    if (mixWithBed && beds.length === 0) {
      api.get("/imaging/assets?kind=bed").then((r) => setBeds(r.data || []));
    }
  }, [mixWithBed, beds.length]);

  // Update #107 — Generate a Mubert bed sized to match the current voice
  // take (mood/tone inferred from the script generator settings; duration
  // padded 4-8s over the voice length so ducking has natural head/tail).
  const generateMatchingBed = async () => {
    if (!lastAsset) { toast.error("Generate a voice take first"); return; }
    // Estimate voice duration from the asset payload; default to 30s.
    const voiceSec =
      Number(lastAsset.duration_sec) ||
      (Number(lastAsset.duration_ms) ? Number(lastAsset.duration_ms) / 1000 : 0) ||
      (Number(lastAsset.size_bytes) ? Number(lastAsset.size_bytes) / 16000 : 30);
    const bedDuration = Math.min(600, Math.max(10, Math.round(voiceSec + 6)));
    // Map script tone → Mubert mood/intensity so the bed matches the read.
    const toneMap = {
      energetic: { mood: "energetic", intensity: "high" },
      calm:      { mood: "chill",     intensity: "low" },
      cinematic: { mood: "cinematic", intensity: "medium" },
      soft:      { mood: "soft",      intensity: "low" },
      hype:      { mood: "hype",      intensity: "high" },
      urgent:    { mood: "urgent",    intensity: "high" },
    };
    const { mood, intensity } = toneMap[scriptTone] || { mood: scriptTone || "neutral", intensity: "medium" };
    setAiBedBusy(true);
    try {
      const { data } = await api.post("/imaging/generate/mubert", {
        genre: "All",
        mood,
        intensity,
        description: `soft under-vocal bed for ${scriptPieceType || "spoken imaging"}, ${bedDuration}s`,
        duration: bedDuration,
      });
      // Refresh the beds list and auto-select the new one.
      const r = await api.get("/imaging/assets?kind=bed");
      const list = r.data || [];
      setBeds(list);
      // Broadcast so the Beds & FX tab reloads too.
      window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
      // Turn on Mix-with-bed and select the fresh bed by genre.
      setMixWithBed(true);
      if (data?.genre) setBedGenre(data.genre); // usually falls back to "auto"
      toast.success(`Fresh bed ready · ${data?.label || "Mubert bed"} added to your library`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "byok_required") {
        toast.error("Mubert quota exhausted — connect your Company key in AI Provider Center.", { duration: 6000 });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Bed generation failed"));
      }
    } finally {
      setAiBedBusy(false);
    }
  };

  const bedGenres = useMemo(() => {
    const set = new Set(beds.map((b) => b.genre).filter(Boolean));
    return Array.from(set).sort();
  }, [beds]);

  const run = async () => {
    if (!text.trim()) { toast.error("Text required"); return; }
    const meta = voices.find((v) => v.id === voice);
    if (meta?.needs_key) { toast.error("This voice needs ElevenLabs API key. Configure it in Integrations."); return; }
    setLoading(true);
    setCartResult(null);
    try {
      const res = await api.post("/imaging/voice/generate", { text, voice, speed, hd });
      setLastAsset(res.data);
      toast.success("Voice take recorded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const sendToNowPlaying = async () => {
    if (!lastAsset?.asset_id) return;
    setSendingCart(true);
    try {
      const payload = {
        piece_type: cartType,
        auto_enable_attach: true,
        mix_with_bed: mixWithBed,
        bed_genre: mixWithBed && bedGenre !== "auto" ? bedGenre : null,
        mix_style: mixStyle,
      };
      // Update #113 — Series mode. Either point at an existing series by id,
      // or pass a name to auto-create one. Backend upserts by name so
      // repeat sends to "Morning show sweepers" stack into the same series.
      if (seriesSelectedId === "__new__" && seriesNewName.trim()) {
        payload.series_name = seriesNewName.trim().slice(0, 120);
      } else if (seriesSelectedId && seriesSelectedId !== "__new__") {
        payload.series_id = seriesSelectedId;
      }
      const res = await api.post(
        `/imaging/voice/${lastAsset.asset_id}/to-cart`,
        payload,
      );
      setCartResult(res.data);
      const seriesLbl = res.data?.series?.name ? ` · series “${res.data.series.name}”` : "";
      const verb = res.data.mix?.mixed ? "Mixed & sent" : "Sent";
      toast.success(`${verb} — cart ${res.data.piece.cart_id} active${seriesLbl}`);
      // If we created a new series, jump the picker to it so the next
      // ship attaches automatically without re-typing.
      if (res.data?.series?.series_id) {
        setSeriesSelectedId(res.data.series.series_id);
        setSeriesNewName("");
        refreshSeries();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send to Now Playing");
    } finally {
      setSendingCart(false);
    }
  };

  // Update #108 — "Ship it" mega-button. Chains everything the DJ would
  // normally do manually after a voice take:
  //   1. Generate a fresh Mubert bed sized/toned to match the read
  //   2. Mix the voice + bed with the currently-selected mix style
  //   3. Send to Now-Playing cart with auto-attach=on so the platform
  //      pushes it into the next track-change rotation immediately.
  // Falls forward on any single-step failure — if bed generation fails
  // (quota, network), still sends the raw voice cart so the DJ isn't blocked.
  const shipIt = async () => {
    if (!lastAsset?.asset_id) { toast.error("Generate a voice take first"); return; }
    setShipping(true);
    let bedGenerated = false;
    try {
      // Step 1 — bed
      setShipStage("bed");
      try {
        await generateMatchingBed();
        bedGenerated = true;
      } catch { /* fall-forward */ }
      // Step 2 + 3 — mix + cart (setSendingCart handled inside sendToNowPlaying)
      setShipStage("cart");
      // sendToNowPlaying reads `mixWithBed` and `bedGenre` — generateMatchingBed
      // just flipped them on so the same call chain does the mix.
      await sendToNowPlaying();
      const parts = [];
      if (bedGenerated) parts.push("bed");
      parts.push("mixed");
      parts.push("live in rotation");
      // If a series is selected/created, name it in the toast so the DJ
      // sees confirmation the piece landed in the right day-part bucket.
      const activeSeriesName =
        seriesSelectedId === "__new__" ? seriesNewName.trim() :
        (seriesList.find((s) => s.series_id === seriesSelectedId)?.name || "");
      const suffix = activeSeriesName ? ` → series “${activeSeriesName}”` : "";
      toast.success(`🚀 Shipped — ${parts.join(" · ")}${suffix}`);
    } catch (e) {
      toast.error("Ship-it failed midway — inspect the individual step buttons");
    } finally {
      setShipStage("");
      setShipping(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="im-voice-form">
        {/* Mode switcher — AI TTS vs Own voice */}
        <div className="rounded-lg border border-line bg-bg-elevated p-1 grid grid-cols-3 gap-1" data-testid="im-voice-mode-switch">
          {[
            { id: "ai",     label: "AI Voice",       icon: Sparkles, hint: "OpenAI TTS · 9 voices" },
            { id: "record", label: "Record yours",   icon: Mic,      hint: "Use your mic" },
            { id: "upload", label: "Upload yours",   icon: Upload,   hint: "MP3 · WAV · M4A" },
          ].map((m) => {
            const Icon = m.icon;
            const active = voiceMode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setVoiceMode(m.id)}
                data-testid={`im-voice-mode-${m.id}`}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-md text-[11px] transition ${active ? "bg-brand-amber text-black font-semibold" : "text-ink-secondary hover:bg-bg"}`}
              >
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{m.label}</span>
                </div>
                <span className={`text-[10px] ${active ? "text-black/70" : "text-ink-muted"}`}>{m.hint}</span>
              </button>
            );
          })}
        </div>

        {voiceMode !== "ai" && (
          <OwnVoiceRecorder mode={voiceMode} onAsset={(a) => { setLastAsset(a); setCartResult(null); }} />
        )}

        {voiceMode === "ai" && (
        <>
        <div>
          <div className="flex flex-wrap items-end justify-between gap-2 mb-1">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Script (max 4096 chars)</Label>
            <div className="flex items-center gap-2">
              <Select value={scriptPieceType} onValueChange={setScriptPieceType}>
                <SelectTrigger className="h-7 px-2 py-0 text-[11px] bg-bg border-line w-[150px]" data-testid="im-voice-script-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  {(pieceTypes.length ? pieceTypes : [
                    {id:"sweeper",label:"Sweeper"},
                    {id:"station_id",label:"Station Id"},
                    {id:"jingle",label:"Jingle"},
                    {id:"dj_drop",label:"Dj Drop"},
                    {id:"promo",label:"Promo"},
                    {id:"podcast_intro",label:"Podcast Intro"},
                    {id:"top_of_hour",label:"Top Of Hour"},
                    {id:"weather_intro",label:"Weather Intro"},
                    {id:"news_intro",label:"News Intro"},
                    {id:"show_opener",label:"Show Opener"},
                    {id:"sponsor_tag",label:"Sponsor Tag"},
                  ]).map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={scriptTone} onValueChange={setScriptTone}>
                <SelectTrigger className="h-7 px-2 py-0 text-[11px] bg-bg border-line w-[110px]" data-testid="im-voice-script-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  {["energetic","smooth","authoritative","playful","urgent","warm"].map((t) => (
                    <SelectItem key={t} value={t} className="text-xs capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={generateScript}
                disabled={scriptBusy}
                data-testid="im-voice-script-ai"
                className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2.5 py-1 text-[11px] font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
                title="Generate a broadcast-ready script for the selected piece type"
              >
                {scriptBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                {scriptBusy ? "Writing…" : "AI script"}
              </button>
            </div>
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 4096))} rows={6} className="mt-1 bg-bg border-line" placeholder="Paste your imaging script here, or hit AI script to draft one for your station…" data-testid="im-voice-text" maxLength={4096} />
          <div className="text-[10px] font-mono text-ink-muted mt-1">{text.length} / 4096</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Voice</Label>
          <Select value={voice} onValueChange={setVoice}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="im-voice-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line max-h-80">
              {voices.map((v) => (
                <SelectItem key={v.id} value={v.id} disabled={v.needs_key}>
                  {v.label}{v.needs_key ? " 🔒" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Speed: <span className="font-mono text-brand-amber">{speed.toFixed(2)}x</span></Label>
            <Slider min={0.5} max={2.0} step={0.05} value={[speed]} onValueChange={(v) => setSpeed(v[0])} className="mt-3" data-testid="im-voice-speed" />
          </div>
          <div className="flex items-end justify-between rounded-lg border border-line bg-bg-elevated p-3">
            <div>
              <div className="text-xs">HD quality</div>
              <div className="text-[10px] text-ink-muted">tts-1-hd · slower</div>
            </div>
            <Switch checked={hd} onCheckedChange={setHd} data-testid="im-voice-hd" />
          </div>
        </div>
        <button onClick={run} disabled={loading || !text.trim()} data-testid="im-voice-generate" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
          {loading ? "Recording..." : "Record voice take"}
        </button>
        </>
        )}
      </div>

      <div data-testid="im-voice-output">
        {!lastAsset && (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted type-body">
            Your voice take will appear here. Then go to Mixer to add bed & FX.
          </div>
        )}
        {lastAsset && (
          <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-5" data-testid="im-voice-take">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">// VOICE TAKE</div>
              {lastAsset.watermarked && (
                <WatermarkUnlockPrompt kind="audio" label={lastAsset.label} />
              )}
            </div>
            <div className="type-list-title text-ink-primary mb-1 line-clamp-2">{lastAsset.label}</div>
            <div className="type-list-subtitle text-ink-muted mb-3 font-mono">{lastAsset.voice_label}</div>
            <AssetPlayer asset={lastAsset} />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={generateMatchingBed}
                disabled={aiBedBusy || shipping}
                data-testid="im-voice-generate-matching-bed"
                title="Generate a fresh Mubert AI music bed sized to this voice take"
                className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 type-button-label text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50 transition"
              >
                {aiBedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Music2 className="h-3.5 w-3.5" />}
                {aiBedBusy ? "Generating bed…" : "Generate matching bed (Mubert)"}
              </button>
              <button
                type="button"
                onClick={shipIt}
                disabled={shipping || aiBedBusy}
                data-testid="im-voice-ship-it"
                title="Bed + mix + push to Now Playing rotation — one shot"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow hover:bg-brand-amber/90 disabled:opacity-50 transition"
              >
                {shipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                {shipping
                  ? (shipStage === "bed" ? "Rendering bed…" : shipStage === "cart" ? "Mixing & shipping…" : "Shipping…")
                  : "🚀 Ship it"}
              </button>
              <span className="type-timestamp text-ink-muted font-mono">
                Bed → mix → live in rotation, one click
              </span>
            </div>
            <div className="text-[10px] text-ink-muted font-mono mt-2">Saved as <span className="text-brand-amber">{lastAsset.asset_id}</span> · Use in Mixer.</div>

            {/* Send to Now Playing */}
            <div className="mt-4 rounded-lg border border-line bg-bg-elevated p-4 space-y-3" data-testid="im-voice-send-np">
              <div className="flex items-center gap-2">
                <Rss className="h-3.5 w-3.5 text-brand-amber" />
                <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">Send to Now Playing</div>
              </div>
              <div className="type-body text-ink-secondary">
                Promote this voice take into a Now Playing cart. It&apos;ll auto-attach to social posts on the next track change.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <Select value={cartType} onValueChange={setCartType}>
                  <SelectTrigger className="bg-bg border-line" data-testid="im-voice-cart-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-elevated border-line max-h-72">
                    {CART_PIECE_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={sendToNowPlaying}
                  disabled={sendingCart}
                  data-testid="im-voice-send-np-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-black hover:bg-brand-amber/90 disabled:opacity-50 transition"
                >
                  {sendingCart ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rss className="h-3.5 w-3.5" />}
                  {sendingCart ? "Sending…" : "Send to Now Playing"}
                </button>
              </div>

              {/* Update #113 — Series picker. Group carts into named series
                  (e.g. "Morning show sweepers") so batch production runs
                  rotate cleanly through the Now-Playing engine. */}
              <div className="rounded-md border border-line bg-bg p-3 space-y-2" data-testid="im-voice-series-picker">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="type-list-title text-ink-primary">Series</div>
                    <div className="type-list-subtitle text-ink-muted">
                      Group this cart into a named series for batch day-part rotation. Optional.
                    </div>
                  </div>
                  {seriesSelectedId && (
                    <button
                      type="button"
                      onClick={() => { setSeriesSelectedId(""); setSeriesNewName(""); }}
                      data-testid="im-voice-series-clear"
                      className="type-timestamp text-ink-muted hover:text-brand-amber transition font-mono"
                      title="Clear series selection"
                    >
                      Clear ×
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                  <Select
                    value={seriesSelectedId || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") { setSeriesSelectedId(""); setSeriesNewName(""); }
                      else setSeriesSelectedId(v);
                    }}
                  >
                    <SelectTrigger className="bg-bg border-line" data-testid="im-voice-series-select"><SelectValue placeholder="No series" /></SelectTrigger>
                    <SelectContent className="bg-bg-elevated border-line max-h-72">
                      <SelectItem value="__none__">No series (one-off cart)</SelectItem>
                      <SelectItem value="__new__">+ New series…</SelectItem>
                      {seriesList.length > 0 && (
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-muted font-mono">Existing</div>
                      )}
                      {seriesList.map((s) => (
                        <SelectItem key={s.series_id} value={s.series_id}>
                          {s.name} · <span className="font-mono text-ink-muted">{s.piece_count} pcs</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {seriesSelectedId === "__new__" && (
                    <input
                      type="text"
                      value={seriesNewName}
                      onChange={(e) => setSeriesNewName(e.target.value.slice(0, 120))}
                      placeholder="e.g. Morning show sweepers"
                      maxLength={120}
                      data-testid="im-voice-series-new-name"
                      className="rounded-md border border-line bg-bg px-3 py-2 type-body text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber transition"
                    />
                  )}
                </div>
                {seriesSelectedId === "__new__" && seriesNewName.trim() && (
                  <div className="type-timestamp text-brand-amber/80 font-mono" data-testid="im-voice-series-hint">
                    → creates “{seriesNewName.trim()}” on next Ship-it
                  </div>
                )}
                {seriesSelectedId && seriesSelectedId !== "__new__" && (
                  <div className="type-timestamp text-ink-muted font-mono" data-testid="im-voice-series-hint">
                    → will append to “{seriesList.find((s) => s.series_id === seriesSelectedId)?.name || "selected series"}”
                  </div>
                )}
              </div>

              {/* Mix-with-bed toggle */}
              <div className="rounded-md border border-line bg-bg p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="type-list-title text-ink-primary">Mix with music bed</div>
                    <div className="type-list-subtitle text-ink-muted">
                      Auto-pick a bed, duck under voice, normalize — ship a fully produced cart in one click.
                    </div>
                  </div>
                  <Switch
                    checked={mixWithBed}
                    onCheckedChange={setMixWithBed}
                    data-testid="im-voice-mix-bed-toggle"
                  />
                </div>
                {mixWithBed && (
                  <div className="space-y-3" data-testid="im-voice-bed-genre-row">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Bed selection</Label>
                        <Select value={bedGenre} onValueChange={setBedGenre}>
                          <SelectTrigger className="bg-bg border-line" data-testid="im-voice-bed-genre"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-bg-elevated border-line max-h-72">
                            <SelectItem value="auto">Auto-pick (first available)</SelectItem>
                            {bedGenres.map((g) => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Mix style</Label>
                        <Select value={mixStyle} onValueChange={setMixStyle}>
                          <SelectTrigger className="bg-bg border-line" data-testid="im-voice-mix-style"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-bg-elevated border-line max-h-72">
                            {(mixPresets.length > 0 ? mixPresets : [
                              { id: "punchy", label: "Punchy" },
                              { id: "smooth", label: "Smooth" },
                              { id: "whisper", label: "Whisper" },
                              { id: "news", label: "News" },
                            ]).map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {beds.length === 0 && (
                      <div className="text-[10px] text-ink-muted">
                        No beds yet. Upload one in Beds &amp; FX or seed the starter pack.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {cartResult && (
                <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-300" data-testid="im-voice-cart-result">
                  Cart <span className="font-mono font-semibold">{cartResult.piece.cart_id}</span> active.
                  {cartResult.mix?.mixed && (
                    <> Mixed (<span className="font-mono">{cartResult.mix.mix_style_label || cartResult.mix.mix_style}</span>) with <span className="font-mono">{cartResult.mix.bed_label || cartResult.mix.bed_genre || "bed"}</span>.</>
                  )}
                  {cartResult.nowplaying.attach_enabled && (
                    <> Auto-attach enabled for: <span className="font-mono">{cartResult.nowplaying.cart_pool_types.join(", ")}</span>.</>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================
 * ASSETS TAB (upload music beds + FX)
 * ===================================================== */
function AssetsTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("bed");
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genreFilter, setGenreFilter] = useState("All");
  const [normalize, setNormalize] = useState(false);
  const [stemsBusy, setStemsBusy] = useState({});
  const [renamingId, setRenamingId] = useState("");
  const [renameVal, setRenameVal] = useState("");
  const [stemModalAsset, setStemModalAsset] = useState(null); // asset currently opening the mode picker
  const [karaokeAsset, setKaraokeAsset] = useState(null);     // asset currently in Karaoke modal
  const [karaokeByAsset, setKaraokeByAsset] = useState({});   // {source_asset_id: {job_id, source_label, ...}}
  const [karaokeShareJob, setKaraokeShareJob] = useState(null); // {job_id, source_label} for the share modal
  // Update #131: deep-link from Creative Studio → the Karaoke modal
  // pre-fills its "custom" background with the provided image URL.
  const [karaokeBgUrl, setKaraokeBgUrl] = useState(searchParams.get("karaoke_bg") || "");
  const fileRef = useRef();

  // Clear the karaoke_bg URL param once we've captured it so a refresh
  // doesn't keep re-applying the background.
  useEffect(() => {
    if (searchParams.get("karaoke_bg")) {
      const next = new URLSearchParams(searchParams);
      next.delete("karaoke_bg");
      setSearchParams(next, { replace: true });
      toast.message("Custom karaoke background loaded", {
        description: "Pick a bed below and click 🎤 Karaoke to render.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const res = await api.get(`/imaging/assets?kind=${tab}`);
    setAssets(res.data);
  };

  const saveRename = async (a) => {
    const label = renameVal.trim();
    if (!label) { toast.error("Name can't be empty"); return; }
    try {
      await api.patch(`/imaging/assets/${a.asset_id}`, { label });
      setAssets((prev) => prev.map((x) => (x.asset_id === a.asset_id ? { ...x, label } : x)));
      setRenamingId("");
      toast.success("Renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  // ── Load karaoke jobs (succeeded) and index by source_asset_id so we
  // can render a "Share karaoke → socials" chip next to each Bed / FX
  // card that already has a rendered karaoke MP4.
  const loadKaraokeJobs = async () => {
    try {
      const r = await api.get("/production/karaoke?status=succeeded&limit=200");
      const jobs = r.data?.jobs || [];
      // Group by source_asset_id — keep the newest per bed (jobs are
      // returned newest-first so the first match wins).
      const idx = {};
      for (const j of jobs) {
        const sid = j.source_asset_id;
        if (sid && !idx[sid] && j.job_id) idx[sid] = j;
      }
      setKaraokeByAsset(idx);
    } catch { /* noop */ }
  };
  useEffect(() => { loadKaraokeJobs(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const h = () => loadKaraokeJobs();
    window.addEventListener("imaging-assets-refresh", h);
    return () => window.removeEventListener("imaging-assets-refresh", h);
  }, []);
  useEffect(() => { load(); setGenreFilter("All"); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("imaging-assets-refresh", h);
    return () => window.removeEventListener("imaging-assets-refresh", h);
    // eslint-disable-next-line
  }, [tab]);

  // Estimate duration from filesize when the server didn't decode the file.
  // Backend stores `duration_ms` on stems (Demucs returns it) — fall back to
  // the parent asset's duration_sec, then 4s for safety.
  const estimateDurationSec = (asset) => {
    if (asset.duration_ms) return Math.max(0.5, asset.duration_ms / 1000);
    if (asset.duration_sec) return Math.max(0.5, parseFloat(asset.duration_sec));
    // Rough estimate from file size — 128kbps MP3 ≈ 16 KB/s
    return Math.max(0.5, (asset.size_bytes || 32000) / 16000);
  };

  // Push the asset(s) into ps_pending_clips and navigate to the Production Suite.
  // ProductionSuite reads this queue once on mount, then clears it.
  const sendToDaw = (sources) => {
    const items = (Array.isArray(sources) ? sources : [sources]).map((a) => ({
      asset_id:     a.asset_id,
      audio_url:    a.audio_url || `/api/imaging/assets/${a.asset_id}/audio`,
      name:         a.label || a.name || a.asset_id,
      duration_sec: estimateDurationSec(a),
      stem_name:    (a.stem_name || "").toLowerCase() || undefined,
      color:        a.stem_name ? undefined : "#4a9eff",
      source:       a.stem_name ? "stem" : "imaging",
    }));
    let existing = [];
    try { existing = JSON.parse(localStorage.getItem("ps_pending_clips") || "[]"); }
    catch { existing = []; }
    try { localStorage.setItem("ps_pending_clips", JSON.stringify([...existing, ...items])); }
    catch { toast.error("Couldn't queue clips — try again"); return; }
    toast.success(`Sending ${items.length} clip${items.length === 1 ? "" : "s"} to the DAW…`);
    navigate("/dashboard/production-suite");
  };

  // For a parent asset that was already split — pull its stems from any tab
  // (stems are stored across voice/bed/sfx kinds depending on which stem they are).
  const sendAllStemsToDaw = async (parent) => {
    try {
      const [v, b, f] = await Promise.all([
        api.get("/imaging/assets?kind=voice"),
        api.get("/imaging/assets?kind=bed"),
        api.get("/imaging/assets?kind=sfx"),
      ]);
      const all = [...(v.data || []), ...(b.data || []), ...(f.data || [])];
      const stems = all.filter((x) => x.stem_name && x.job_id && parent.job_id && x.job_id === parent.job_id);
      if (stems.length === 0) {
        toast.message("No stems found yet — split this track first.");
        return;
      }
      sendToDaw(stems);
    } catch {
      toast.error("Couldn't load stems");
    }
  };

  // Stem-separate: opens the mode picker modal. Actual dispatch runs
  // via runSplitStems() once the user chooses standard vs advanced.
  const splitStems = (asset) => {
    if (stemsBusy[asset.asset_id]) return;
    setStemModalAsset(asset);
  };

  const runSplitStems = async (advanced) => {
    const asset = stemModalAsset;
    setStemModalAsset(null);
    if (!asset || stemsBusy[asset.asset_id]) return;
    setStemsBusy((s) => ({ ...s, [asset.asset_id]: true }));
    const release = () => setStemsBusy((s) => { const c = { ...s }; delete c[asset.asset_id]; return c; });
    try {
      const r = await api.post("/production/stem-separate", {
        asset_id: asset.asset_id,
        file_name: asset.label || `${asset.asset_id}.wav`,
        advanced: !!advanced,
      });
      const data = r.data || {};
      if (data.stub) {
        toast.message(data.message || "Connect LALAL.AI or Replicate to use stem separation");
        release();
        return;
      }
      if (data.status === "succeeded" && Array.isArray(data.stems) && data.stems.length > 0) {
        const pLabel = data.provider === "lalal_ai" ? "LALAL.AI" : "Demucs (Replicate)";
        toast.success(`Stems ready via ${pLabel} — ${data.stems.length} added to your library`);
        load();
        window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
        release();
        return;
      }
      // Polling path (Replicate async)
      const providerHint = data.provider === "lalal_ai" ? "LALAL.AI" : "Demucs";
      toast.message(`Splitting "${asset.label}" — ${providerHint} is processing…`, { duration: 4000 });
      const started = Date.now();
      const poll = async () => {
        try {
          const s = await api.get(`/production/stem-separate/${data.job_id}`);
          const j = s.data || {};
          if (j.status === "succeeded" && Array.isArray(j.stems) && j.stems.length > 0) {
            const pLabel = j.provider === "lalal_ai" ? "LALAL.AI" : "Demucs (Replicate)";
            toast.success(`Stems ready via ${pLabel} — ${j.stems.length} added to your library`);
            load();
            window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
            release();
            return;
          }
          if (j.status === "failed") {
            toast.error(`Stem separation failed: ${j.error || "unknown error"}`);
            release();
            return;
          }
          if (Date.now() - started > 5 * 60 * 1000) {
            toast.error("Stem separation timed out after 5 minutes");
            release();
            return;
          }
          setTimeout(poll, 3000);
        } catch {
          toast.error("Polling failed — try again");
          release();
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "byok_required") {
        toast.error("Connect LALAL.AI (primary) or Replicate (fallback) in AI Provider Center to split stems", {
          duration: 6000,
          action: { label: "Open", onClick: () => window.open("/dashboard/providers", "_blank") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Stem separation failed"));
      }
      release();
    }
  };

  const upload = async (file, label, genre) => {
    if (!file) return;
    if (file.size > 250 * 1024 * 1024) { toast.error("File > 250 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", tab);
      if (label) fd.append("label", label);
      if (genre) fd.append("genre", genre);
      if (normalize) fd.append("normalize", "true");
      const res = await fetch(`${API_BASE}/imaging/assets/upload`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error("upload failed");
      toast.success(`${KIND_LABEL[tab]} uploaded`);
      load();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/imaging/assets/${id}`);
    setAssets(assets.filter((a) => a.asset_id !== id));
    toast.success("Deleted");
  };

  const filtered = tab === "bed" && genreFilter !== "All"
    ? assets.filter((a) => (a.genre || "").toLowerCase() === genreFilter.toLowerCase())
    : assets;

  return (
    <div data-testid="im-assets-page">
      {/* Stem separation mode picker — 4-stem (Standard) vs 10-stem (Advanced) */}
      <StemSeparateModal
        open={!!stemModalAsset}
        busy={false}
        sourceLabel={stemModalAsset?.label || stemModalAsset?.name || ""}
        onClose={() => setStemModalAsset(null)}
        onConfirm={runSplitStems}
      />

      {/* Studio Karaoke — LALAL.AI + Whisper + FFmpeg one-shot pipeline */}
      <KaraokeModal
        open={!!karaokeAsset}
        asset={karaokeAsset || undefined}
        defaultBackgroundImageUrl={karaokeBgUrl || undefined}
        onClose={() => { setKaraokeAsset(null); setKaraokeBgUrl(""); }}
        onCreated={() => {
          load();
          loadKaraokeJobs();
          window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
        }}
      />

      {/* Reusable karaoke → socials share modal for previously rendered MP4s */}
      <KaraokeShareModal
        open={!!karaokeShareJob}
        jobId={karaokeShareJob?.job_id}
        sourceLabel={karaokeShareJob?.source_label}
        defaultCaption={
          karaokeShareJob
            ? `🎤 ${karaokeShareJob.source_label || "Karaoke edit"}\n\nFresh karaoke drop from the studio.\n\n#Karaoke #Radio #BroadcastFlow`
            : ""
        }
        onClose={() => setKaraokeShareJob(null)}
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {["bed", "opener", "closer", "fx", "voice"].map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`assets-tab-${k}`}
            className={`px-4 py-1.5 text-xs font-medium rounded transition ${tab === k ? "bg-brand-amber text-black" : "bg-bg-surface border border-line text-ink-secondary hover:text-ink-primary"}`}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
        {tab === "bed" && (
          <DailyBedPackButton
            onImported={(items) => {
              if (items?.length) load();
            }}
          />
        )}
      </div>

      {tab === "fx" && (
        <div className="mb-5">
          <SfxBrowser onImport={() => load()} />
        </div>
      )}

      {tab === "bed" && (
        <div className="mb-5">
          <MorningPackPanel
            onRunComplete={() => load()}
            data-testid="morning-pack-panel-wrap"
          />
        </div>
      )}

      {tab === "bed" && (
        <MusicSourcePicker
          onTrackAdded={(asset) => setAssets((prev) => [asset, ...prev])}
          data-testid="im-music-source"
        />
      )}

      <div className="rounded-xl border border-line bg-bg-surface p-6 mb-6" data-testid="im-upload-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber mb-1">// STARTER PACK</div>
            <div className="type-body text-ink-primary">
              One-click drop: 7 broadcast FX, <span className="text-brand-amber">24 demo music beds</span> across 12 categories (R&B, Urban, Pop, Reggae, Hip-Hop, News, Sports, Talk, Smooth Jazz, Dance, Gospel, Caribbean), and 8 branding stingers (4 openers + 4 closers).
            </div>
            <div className="type-list-subtitle text-ink-muted mt-1">Demo beds are AI-synthesized placeholders. A real licensed library (2,500+ cuts) requires commercial music agreements — upload your own catalog when ready.</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={async () => {
                try {
                  const res = await api.post("/imaging/starter-pack/seed");
                  const total = (res.data.fx || 0) + (res.data.bed || 0) + (res.data.opener || 0) + (res.data.closer || 0);
                  toast.success(`Added ${total} new assets (${res.data.skipped} already present)`);
                  window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
                } catch { toast.error("Seed failed"); }
              }}
              data-testid="seed-starter-pack-btn"
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-black amber-glow"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Seed starter pack
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.post("/imaging/more-effects/seed");
                  toast.success(`Bonus FX pack: ${res.data.fx} added (${res.data.skipped} already in library)`);
                  window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
                } catch { toast.error("Bonus FX seed failed"); }
              }}
              data-testid="seed-more-effects-btn"
              className="inline-flex items-center gap-2 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-4 py-2 type-button-label text-brand-amber hover:bg-brand-amber/20 transition"
            >
              <Zap className="h-3.5 w-3.5" />
              Seed bonus FX pack
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.post("/integrations/broadcast_radio_uk/pull-carts");
                  toast.success(`Pulled ${res.data.imported} cart${res.data.imported === 1 ? "" : "s"} from Myriad (${res.data.skipped} already in library)`);
                  window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Pull failed", { duration: 8000 });
                }
              }}
              data-testid="pull-broadcast-radio-btn"
              className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 type-button-label text-cyan-200 hover:bg-cyan-500/20 transition"
            >
              <Radio className="h-3.5 w-3.5" />
              Pull from Broadcast Radio
            </button>
          </div>
        </div>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
          className="rounded-xl border-2 border-dashed border-line hover:border-brand-amber/60 transition cursor-pointer p-8 text-center"
          data-testid="im-asset-dropzone"
        >
          <Upload className="h-8 w-8 mx-auto text-brand-amber mb-3" />
          <div className="type-section-heading text-ink-primary mb-1">
            {tab === "voice" ? "Upload voice-over recording" : `Drop ${KIND_LABEL[tab].toLowerCase()} here`}
          </div>
          <div className="type-card-meta-label text-ink-muted font-mono">MP3 · WAV · M4A · AAC · FLAC · OGG · up to 250 MB</div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".mp3,.wav,.m4a,.aac,.flac,.ogg,audio/*"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="im-asset-file-input"
          />
        </div>
        {tab === "voice" && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-3">
            <div>
              <div className="type-list-title text-ink-primary">Normalize audio level on upload</div>
              <div className="type-list-subtitle text-ink-muted">Recommended for inconsistent home recordings.</div>
            </div>
            <Switch checked={normalize} onCheckedChange={setNormalize} data-testid="upload-normalize-toggle" />
          </div>
        )}
        {busy && <div className="mt-3 text-xs text-brand-amber font-mono animate-pulse">UPLOADING & PROCESSING...</div>}
      </div>

      {tab === "bed" && assets.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="bed-category-filter">
          {BED_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setGenreFilter(cat)}
              data-testid={`bed-cat-${cat}`}
              className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${
                genreFilter === cat
                  ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                  : "border-line bg-bg-surface text-ink-secondary hover:text-ink-primary"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted type-body">
          {tab === "bed" && genreFilter !== "All" ? `No ${genreFilter} beds yet.` : `No ${KIND_LABEL[tab].toLowerCase()}s yet.`}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="im-asset-list">
          {filtered.map((a) => (
            <div key={a.asset_id} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`im-asset-${a.asset_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  {renamingId === a.asset_id ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={renameVal}
                        onChange={(e) => setRenameVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(a); if (e.key === "Escape") setRenamingId(""); }}
                        className="w-full rounded-md border border-brand-amber/50 bg-bg px-2 py-1 text-sm text-ink-primary focus:outline-none"
                        data-testid={`im-asset-rename-input-${a.asset_id}`}
                      />
                      <button onClick={() => saveRename(a)} className="text-emerald-300 hover:text-emerald-200 shrink-0" title="Save name" data-testid={`im-asset-rename-save-${a.asset_id}`}><Check className="h-4 w-4" /></button>
                      <button onClick={() => setRenamingId("")} className="text-ink-muted hover:text-ink-primary shrink-0" title="Cancel"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="type-list-title text-ink-primary truncate">{a.label}</div>
                      <button
                        onClick={() => { setRenamingId(a.asset_id); setRenameVal(a.label || ""); }}
                        className="text-ink-muted hover:text-brand-amber shrink-0 opacity-60 hover:opacity-100 transition"
                        title="Rename this asset — friendly names show up in pickers (Commercials, DAW…)"
                        data-testid={`im-asset-rename-${a.asset_id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <div className="type-list-subtitle font-mono text-ink-muted truncate">
                    {a.voice_label || a.genre || a.content_type} · {(a.size_bytes / 1024).toFixed(0)}KB
                    {a.normalized && <span className="ml-2 text-brand-amber">· NORM</span>}
                    {a.stem_name && <span className="ml-2 text-cyan-300">· STEM</span>}
                  </div>
                  {a.source && a.source !== "ai_generated" && a.source !== "user_uploaded" && (
                    <SourceBadge source={a.source} testId={`im-asset-source-${a.asset_id}`} />
                  )}
                  {a.source === "user_uploaded" && <SourceBadge source="user_uploaded" testId={`im-asset-source-${a.asset_id}`} />}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {a.stem_name ? (
                    <button
                      onClick={() => sendToDaw(a)}
                      title={`Drop this ${a.stem_name} stem onto the Production Suite timeline`}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2 py-1 type-status-badge text-brand-amber hover:bg-brand-amber/20 transition"
                      data-testid={`im-asset-todaw-${a.asset_id}`}
                    >
                      <Layers className="h-3 w-3" /> → DAW
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => splitStems(a)}
                        disabled={!!stemsBusy[a.asset_id]}
                        title="Split this track into vocals / drums / bass / other stems (Replicate)"
                        className="inline-flex items-center gap-1 rounded-md border border-cyan-500/40 bg-cyan-500/5 px-2 py-1 type-status-badge text-cyan-300 hover:bg-cyan-500/15 transition disabled:opacity-50"
                        data-testid={`im-asset-stems-${a.asset_id}`}
                      >
                        {stemsBusy[a.asset_id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Layers3 className="h-3 w-3" />}
                        {stemsBusy[a.asset_id] ? "Splitting…" : "Stems"}
                      </button>
                      {a.job_id && (
                        <button
                          onClick={() => sendAllStemsToDaw(a)}
                          title="Send all 4 stems of this track to the Production Suite"
                          className="inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2 py-1 type-status-badge text-brand-amber hover:bg-brand-amber/20 transition"
                          data-testid={`im-asset-allstems-todaw-${a.asset_id}`}
                        >
                          <Layers className="h-3 w-3" /> Stems → DAW
                        </button>
                      )}
                      {tab === "bed" && (
                        <button
                          onClick={() => setKaraokeAsset(a)}
                          title="Turn this track into a karaoke MP4 (LALAL.AI + Whisper)"
                          className="inline-flex items-center gap-1 rounded-md border border-pink-500/40 bg-pink-500/5 px-2 py-1 type-status-badge text-pink-300 hover:bg-pink-500/15 transition"
                          data-testid={`im-asset-karaoke-${a.asset_id}`}
                        >
                          <Music2 className="h-3 w-3" /> Karaoke
                        </button>
                      )}
                      {karaokeByAsset[a.asset_id] && (
                        <button
                          onClick={() => setKaraokeShareJob(karaokeByAsset[a.asset_id])}
                          title="Share the karaoke MP4 you rendered for this track to socials"
                          className="inline-flex items-center gap-1 rounded-md border border-blue-500/40 bg-blue-500/5 px-2 py-1 type-status-badge text-blue-300 hover:bg-blue-500/15 transition"
                          data-testid={`im-asset-karaoke-share-${a.asset_id}`}
                        >
                          <Share2 className="h-3 w-3" /> Share karaoke
                        </button>
                      )}
                    </>
                  )}
                  <button onClick={() => del(a.asset_id)} className="text-ink-muted hover:text-brand-onair" data-testid={`im-asset-del-${a.asset_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <AssetPlayer asset={a} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =====================================================
 * MIXER TAB
 * ===================================================== */
function MixerTab() {
  const [voiceAssets, setVoiceAssets] = useState([]);
  const [beds, setBeds] = useState([]);
  const [fxList, setFxList] = useState([]);
  const [openers, setOpeners] = useState([]);
  const [closers, setClosers] = useState([]);
  const [types, setTypes] = useState([]);
  const [config, setConfig] = useState({
    voice_asset_id: "",
    bed_asset_id: "",
    opener_asset_id: "",
    closer_asset_id: "",
    fx_asset_ids: [],
    voice_volume_db: 0,
    bed_volume_db: -10,
    fx_volume_db: -3,
    opener_volume_db: -2,
    closer_volume_db: -2,
    bed_fade_in_ms: 600,
    bed_fade_out_ms: 900,
    duck_bed_db: -6,
    normalize_output: false,
    format: "mp3",
    piece_type: "station_id",
    title: "Untitled imaging",
    station_name: "",
    slogan: "",
    frequency: "",
    listen_live_tag: "",
  });
  const [mixing, setMixing] = useState(false);
  const [mixed, setMixed] = useState(null);

  const load = async () => {
    const [v, b, f, o, c, t, s] = await Promise.all([
      api.get("/imaging/assets?kind=voice"),
      api.get("/imaging/assets?kind=bed"),
      api.get("/imaging/assets?kind=fx"),
      api.get("/imaging/assets?kind=opener"),
      api.get("/imaging/assets?kind=closer"),
      api.get("/imaging/types"),
      api.get("/stations/me"),
    ]);
    setVoiceAssets(v.data);
    setBeds(b.data);
    setFxList(f.data);
    setOpeners(o.data);
    setClosers(c.data);
    setTypes(t.data);
    if (s.data?.name && !config.station_name) {
      setConfig((c) => ({ ...c, station_name: s.data.name, slogan: s.data.tagline || "" }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleFx = (id) => setConfig((c) => ({
    ...c,
    fx_asset_ids: c.fx_asset_ids.includes(id) ? c.fx_asset_ids.filter((x) => x !== id) : [...c.fx_asset_ids, id],
  }));

  const mix = async () => {
    if (!config.voice_asset_id && !config.bed_asset_id && !config.opener_asset_id && !config.closer_asset_id && config.fx_asset_ids.length === 0) {
      toast.error("Pick at least one layer"); return;
    }
    setMixing(true);
    setMixed(null);
    try {
      const payload = { ...config };
      ["voice_asset_id", "bed_asset_id", "opener_asset_id", "closer_asset_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      const res = await api.post("/imaging/mix", payload);
      setMixed(res.data);
      toast.success("Mix rendered");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mix failed");
    } finally { setMixing(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6 space-y-6" data-testid="im-mixer-form">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// 1. OPENER</div>
          <Select value={config.opener_asset_id || "none"} onValueChange={(v) => setConfig({ ...config, opener_asset_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-bg border-line" data-testid="im-mix-opener"><SelectValue placeholder="Select opener (optional)" /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              <SelectItem value="none">— No opener —</SelectItem>
              {openers.map((a) => <SelectItem key={a.asset_id} value={a.asset_id}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SliderRow label="Opener volume" value={config.opener_volume_db} onChange={(v) => setConfig({ ...config, opener_volume_db: v })} min={-12} max={6} unit="dB" testid="im-mix-opener-vol" />
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// 2. VOICE TRACK</div>
          <Select value={config.voice_asset_id || "none"} onValueChange={(v) => setConfig({ ...config, voice_asset_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-bg border-line" data-testid="im-mix-voice"><SelectValue placeholder="Select voice take or upload" /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              <SelectItem value="none">— No voice —</SelectItem>
              {voiceAssets.map((a) => <SelectItem key={a.asset_id} value={a.asset_id}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SliderRow label="Voice volume" value={config.voice_volume_db} onChange={(v) => setConfig({ ...config, voice_volume_db: v })} min={-12} max={6} unit="dB" testid="im-mix-voice-vol" />
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// 3. MUSIC BED</div>
          <Select value={config.bed_asset_id || "none"} onValueChange={(v) => setConfig({ ...config, bed_asset_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-bg border-line" data-testid="im-mix-bed"><SelectValue placeholder="Select music bed" /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line max-h-72">
              <SelectItem value="none">— No bed —</SelectItem>
              {beds.map((a) => <SelectItem key={a.asset_id} value={a.asset_id}>{a.label}{a.genre ? ` · ${a.genre}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <SliderRow label="Bed volume" value={config.bed_volume_db} onChange={(v) => setConfig({ ...config, bed_volume_db: v })} min={-24} max={6} unit="dB" testid="im-mix-bed-vol" />
          <SliderRow label="Duck under voice" value={config.duck_bed_db} onChange={(v) => setConfig({ ...config, duck_bed_db: v })} min={-18} max={0} unit="dB" testid="im-mix-duck" />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <SliderRow label="Fade in" value={config.bed_fade_in_ms} onChange={(v) => setConfig({ ...config, bed_fade_in_ms: v })} min={0} max={3000} step={50} unit="ms" testid="im-mix-fadein" />
            <SliderRow label="Fade out" value={config.bed_fade_out_ms} onChange={(v) => setConfig({ ...config, bed_fade_out_ms: v })} min={0} max={3000} step={50} unit="ms" testid="im-mix-fadeout" />
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// 4. CLOSER</div>
          <Select value={config.closer_asset_id || "none"} onValueChange={(v) => setConfig({ ...config, closer_asset_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-bg border-line" data-testid="im-mix-closer"><SelectValue placeholder="Select closer (optional)" /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              <SelectItem value="none">— No closer —</SelectItem>
              {closers.map((a) => <SelectItem key={a.asset_id} value={a.asset_id}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SliderRow label="Closer volume" value={config.closer_volume_db} onChange={(v) => setConfig({ ...config, closer_volume_db: v })} min={-12} max={6} unit="dB" testid="im-mix-closer-vol" />
        </div>

        <div>
          <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber mb-2">// 5. SOUND FX (optional)</div>
          {fxList.length === 0 ? (
            <div className="type-body text-ink-muted">Upload FX in the Beds & FX tab or seed the starter pack.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {fxList.map((f) => (
                <button
                  key={f.asset_id}
                  onClick={() => toggleFx(f.asset_id)}
                  data-testid={`im-mix-fx-${f.asset_id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs transition ${
                    config.fx_asset_ids.includes(f.asset_id)
                      ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                      : "border-line bg-bg-elevated text-ink-secondary"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {fxList.length > 0 && (
            <SliderRow label="FX volume" value={config.fx_volume_db} onChange={(v) => setConfig({ ...config, fx_volume_db: v })} min={-18} max={6} unit="dB" testid="im-mix-fx-vol" />
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-3" data-testid="im-mix-branding">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// STATION BRANDING</div>
          <Inp label="Station name" v={config.station_name} on={(x) => setConfig({ ...config, station_name: x })} testid="im-mix-station-name" />
          <Inp label="Slogan" v={config.slogan} on={(x) => setConfig({ ...config, slogan: x })} testid="im-mix-slogan" />
          <Inp label="Frequency / website" v={config.frequency} on={(x) => setConfig({ ...config, frequency: x })} testid="im-mix-frequency" />
          <Inp label="Listen-live tag" v={config.listen_live_tag} on={(x) => setConfig({ ...config, listen_live_tag: x })} testid="im-mix-listen-tag" />
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-3">// EXPORT</div>
          <Inp label="Title" v={config.title} on={(x) => setConfig({ ...config, title: x })} testid="im-mix-title" />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Type</Label>
              <Select value={config.piece_type} onValueChange={(v) => setConfig({ ...config, piece_type: v })}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="im-mix-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Format</Label>
              <Select value={config.format} onValueChange={(v) => setConfig({ ...config, format: v })}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="im-mix-format"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  <SelectItem value="mp3">MP3 · 192 kbps</SelectItem>
                  <SelectItem value="wav">WAV · 16-bit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-3">
            <div>
              <div className="type-list-title text-ink-primary">Loudness normalize</div>
              <div className="type-list-subtitle text-ink-muted">Even out final master to broadcast level.</div>
            </div>
            <Switch checked={config.normalize_output} onCheckedChange={(v) => setConfig({ ...config, normalize_output: v })} data-testid="im-mix-normalize" />
          </div>
          <button
            onClick={mix}
            disabled={mixing}
            data-testid="im-mix-render"
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-5 py-3 type-button-label text-black amber-glow disabled:opacity-50"
          >
            {mixing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {mixing ? "Rendering..." : "Render & save mix"}
          </button>
        </div>

        {mixed && (
          <div className="rounded-xl border border-brand-amber/50 bg-bg-surface p-5" data-testid="im-mix-result">
            <div className="font-mono type-status-badge uppercase tracking-wider text-brand-amber mb-2">// MIX READY · {(mixed.duration_ms / 1000).toFixed(1)}s</div>
            <div className="type-list-title text-ink-primary mb-3">{mixed.title}</div>
            <PiecePlayer piece={mixed} />
            {mixed.branding?.station_name && (
              <div className="mt-3 pt-3 border-t border-line text-[10px] font-mono text-ink-muted">
                {mixed.branding.station_name}{mixed.branding.frequency ? ` · ${mixed.branding.frequency}` : ""}
                {mixed.branding.slogan && <div className="text-ink-secondary mt-1">{mixed.branding.slogan}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================
 * LIBRARY TAB
 * ===================================================== */
function LibraryTab() {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/imaging/pieces");
      setPieces(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/imaging/pieces/${id}`);
    setPieces(pieces.filter((p) => p.piece_id !== id));
    toast.success("Removed");
  };

  if (loading) return <div className="text-center py-12 font-mono type-status-badge text-ink-muted">LOADING LIBRARY...</div>;

  if (pieces.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted type-body" data-testid="im-library-empty">
        <LibraryIcon className="h-8 w-8 mx-auto mb-3 opacity-50" />
        Your imaging library is empty. Render a mix in the Mixer tab.
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="im-library-grid">
      {pieces.map((p) => (
        <div key={p.piece_id} className="rounded-xl border border-line bg-bg-surface p-5 lift" data-testid={`im-piece-${p.piece_id}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="type-list-title text-ink-primary truncate">{p.title}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {p.cart_id && (
                  <span className="rounded-full bg-brand-onair/15 text-brand-onair border border-brand-onair/30 px-2 py-0.5 font-mono text-[10px] tracking-wider" title="Automation cart ID">
                    {p.cart_id}
                  </span>
                )}
                <span className="rounded-full bg-brand-amber/15 text-brand-amber px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">{p.piece_type.replace(/_/g, " ")}</span>
                <span className="font-mono text-[10px] text-ink-muted">{(p.duration_ms / 1000).toFixed(1)}s · {p.format.toUpperCase()}</span>
              </div>
            </div>
            <button onClick={() => del(p.piece_id)} className="text-ink-muted hover:text-brand-onair shrink-0" data-testid={`im-piece-del-${p.piece_id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <PiecePlayer piece={p} />
        </div>
      ))}
    </div>
  );
}

/* =====================================================
 * Reusable bits
 * ===================================================== */
function Inp({ label, v, on, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
      <Input value={v || ""} onChange={(e) => on(e.target.value)} className="mt-1 bg-bg border-line" data-testid={testid} />
    </div>
  );
}

function SliderRow({ label, value, onChange, min, max, step = 1, unit, testid }) {
  return (
    <div className="mt-3">
      <Label className="text-xs uppercase tracking-wider text-ink-secondary flex justify-between">
        <span>{label}</span>
        <span className="font-mono text-brand-amber">{value}{unit}</span>
      </Label>
      <Slider min={min} max={max} step={step} value={[value]} onValueChange={(v) => onChange(v[0])} className="mt-2" data-testid={testid} />
    </div>
  );
}

function AssetPlayer({ asset }) {
  return <AudioFetcher path={`/imaging/assets/${asset.asset_id}/audio`} label={asset.label} testid={`audio-${asset.asset_id}`} />;
}
function PiecePlayer({ piece }) {
  return <AudioFetcher path={`/imaging/pieces/${piece.piece_id}/audio`} label={piece.title} downloadName={`${(piece.title || "imaging").replace(/[^a-z0-9]+/gi, "-")}.${piece.format}`} testid={`audio-${piece.piece_id}`} />;
}

function AudioFetcher({ path, label, downloadName, testid }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef();
  // Stable id so the global "preview-started" event can identify this player
  // and other players can know to pause themselves when someone else starts.
  const myIdRef = useRef(`audio_${path}_${Math.random().toString(36).slice(2, 8)}`);

  const ensureLoaded = async () => {
    if (blobUrl) return blobUrl;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error("audio fetch");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      return url;
    } catch {
      toast.error("Audio load failed");
      return null;
    } finally { setLoading(false); }
  };

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, [blobUrl]);

  // Pause whenever another preview starts elsewhere OR a global stop fires.
  // This is what makes the Now-Previewing sticky bar work end-to-end: only
  // one audio plays anywhere in the dashboard at any time.
  useEffect(() => {
    const onStopAll = () => {
      const a = audioRef.current;
      if (a && !a.paused) { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } }
      setPlaying(false);
    };
    const onPeerStart = (e) => {
      const peerId = e?.detail?.id;
      if (!peerId || peerId === myIdRef.current) return;
      const a = audioRef.current;
      if (a && !a.paused) { try { a.pause(); } catch { /* noop */ } }
      setPlaying(false);
    };
    window.addEventListener("bfa:ps-stop-all-preview", onStopAll);
    window.addEventListener("bfa:preview-started", onPeerStart);
    return () => {
      window.removeEventListener("bfa:ps-stop-all-preview", onStopAll);
      window.removeEventListener("bfa:preview-started", onPeerStart);
    };
  }, []);

  // Tear down on unmount so navigating away kills the audio + dismisses the bar.
  useEffect(() => () => {
    const a = audioRef.current;
    if (a && !a.paused) { try { a.pause(); } catch { /* noop */ } }
    try { window.dispatchEvent(new CustomEvent("bfa:preview-ended", { detail: { id: myIdRef.current } })); } catch { /* noop */ }
  }, []);

  const toggle = async () => {
    const url = await ensureLoaded();
    if (!url) return;
    let a = audioRef.current;
    // First click: wait for React to mount the <audio> element after setBlobUrl
    for (let i = 0; !a && i < 10; i++) {
      await new Promise((r) => setTimeout(r, 50));
      a = audioRef.current;
    }
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      // Bar stays visible (paused) so the user can resume from it.
    } else {
      if (a.ended) a.currentTime = 0;
      a.play().catch(() => { /* noop */ });
      setPlaying(true);
      const dur = (a.duration && isFinite(a.duration)) ? a.duration : 30;
      try {
        window.dispatchEvent(new CustomEvent("bfa:preview-started", {
          detail: {
            id: myIdRef.current,
            label: label || downloadName || "Imaging audio",
            source: "imaging",
            color: "#F5A524",
            durationMs: Math.round(dur * 1000),
            media: a,
          },
        }));
      } catch { /* noop */ }
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    try { window.dispatchEvent(new CustomEvent("bfa:preview-ended", { detail: { id: myIdRef.current } })); } catch { /* noop */ }
  };

  // Download as a REAL anchor link. The backend `?download=1` query sets
  // `Content-Disposition: attachment` so the browser saves the file even if
  // the `download` attribute is stripped by extensions / enterprise security
  // software. Cookies are sent automatically (same-origin). This works in
  // every browser, including ones that block JS-driven `.click()` downloads.
  const downloadHref = `${API_BASE}${path}${path.includes("?") ? "&" : "?"}download=1`;

  return (
    <div className="flex items-center gap-2">
      <button onClick={toggle} disabled={loading} data-testid={`${testid}-play`} className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow disabled:opacity-50">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        {playing ? "Pause" : "Play"}
      </button>
      <a
        href={downloadHref}
        download={downloadName || "audio"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-download`}
        className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label hover:text-cyan-300 transition"
      >
        <Download className="h-3.5 w-3.5" /> Download
      </a>
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          onEnded={handleEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          className="hidden"
        />
      )}
    </div>
  );
}
